/**
 * FormattingMarks — a toolbar toggle that shows/hides formatting marks, like MS Word's
 * "Show/Hide ¶": the paragraph mark (¶) at the end of each paragraph and the line-break
 * mark (↵) at every Shift+Enter soft break.
 *
 * The ¶ is pure CSS (.ck-content.ck-show-pilcrow p::after). The ↵ cannot be done in CSS
 * because a <br> renders neither generated content nor a background, so the soft break is
 * re-rendered in the EDITING view only as an opaque wrapper holding the mark plus the <br>.
 * The data pipeline is deliberately left alone, so getData(), the saved content and the
 * Word/PDF export still contain a plain <br> and are unaffected by this feature.
 */
import { Plugin } from '@ckeditor/ckeditor5-core';
import { ButtonView } from '@ckeditor/ckeditor5-ui';

const PILCROW_ICON =
	'<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">' +
	'<text x="3" y="16" font-family="Arial, Helvetica, sans-serif" font-size="16" ' +
	'font-weight="bold" fill="currentColor">¶</text></svg>';

export default class FormattingMarks extends Plugin {
	public static get pluginName() {
		return 'FormattingMarks' as const;
	}

	public init(): void {
		const editor = this.editor;
		let isOn = false;

		// The ↵ rendering is opt-in per editor via `formattingMarks: { enabled: true }`. This
		// plugin ships in the build, so it loads in every editor; without the flag the
		// editing view is left completely untouched. The button below is always registered,
		// so listing the toolbar item can never fail.
		//
		// Editing view only (never 'downcast', which would also change the data), and at
		// 'high' priority so it consumes the soft break before ShiftEnter's default <br>
		// converter. A raw element is used because its inner DOM is opaque to CKEditor:
		// the mark cannot be typed into and, like the <br> it replaces, it holds no model
		// content, so selection treats it exactly as the plain soft break it renders.
		if ( editor.config.get( 'formattingMarks.enabled' ) ) {
			editor.conversion.for( 'editingDowncast' ).elementToElement( {
				model: 'softBreak',
				view: ( modelElement, { writer } ) => writer.createRawElement(
					'span',
					{ class: 'ck-soft-break' },
					domElement => {
						domElement.innerHTML = '<span class="ck-soft-break__mark">↵</span><br>';
					}
				),
				converterPriority: 'high'
			} );
		}

		editor.ui.componentFactory.add( 'formattingMarks', locale => {
			const button = new ButtonView( locale );

			button.set( {
				label: 'Formatting marks',
				icon: PILCROW_ICON,
				tooltip: true,
				isToggleable: true
			} );

			button.on( 'execute', () => {
				isOn = !isOn;
				button.isOn = isOn;

				editor.editing.view.change( writer => {
					const root = editor.editing.view.document.getRoot();

					if ( !root ) {
						return;
					}

					if ( isOn ) {
						writer.addClass( 'ck-show-pilcrow', root );
					} else {
						writer.removeClass( 'ck-show-pilcrow', root );
					}
				} );
			} );

			return button;
		} );
	}
}
